import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Users, Plus, Search, Loader2, Building2, Mail,
  List, Columns3, GripVertical, Calendar, ExternalLink, MessageSquare,
} from 'lucide-react';

interface AdvisorClient {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  status: string;
  status_display: string;
  customer_organization_id: string | null;
  engagement_start: string | null;
  next_renewal: string | null;
  referral_source: string;
  contact_message: string;
  scheduled_call_date: string | null;
  scheduled_call_url: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

const KANBAN_COLUMNS = [
  { key: 'lead', label: 'Lead', color: 'border-slate-300' },
  { key: 'contacted', label: 'Contacted', color: 'border-blue-300' },
  { key: 'qualified', label: 'Qualified', color: 'border-amber-300' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: 'border-purple-300' },
  { key: 'onboarded', label: 'Onboarded', color: 'border-green-300' },
];

const STATUS_BADGE_COLORS: Record<string, string> = {
  lead: 'bg-slate-500/10 text-slate-700 border-slate-500/30',
  contacted: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
  qualified: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  proposal_sent: 'bg-purple-500/10 text-purple-700 border-purple-500/30',
  onboarded: 'bg-green-500/10 text-green-700 border-green-500/30',
};

function InlineNotes({ notes, onSave }: { notes: string; onSave: (notes: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(notes || '');

  if (editing) {
    return (
      <div className="space-y-1">
        <textarea
          className="w-full text-xs rounded border bg-background px-2 py-1 min-h-[40px] resize-y"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Add a note..."
          autoFocus
          data-testid="note-editor"
        />
        <div className="flex gap-1">
          <Button size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { onSave(value); setEditing(false); }}>Save</Button>
          <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5" onClick={() => { setValue(notes || ''); setEditing(false); }}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <p
      className="text-xs text-muted-foreground line-clamp-3 cursor-pointer hover:text-foreground"
      onClick={() => setEditing(true)}
      title="Click to edit note"
      data-testid="inline-note"
    >
      {notes || <span className="italic text-muted-foreground/50">Click to add note...</span>}
    </p>
  );
}

function ClientCard({
  client,
  onDragStart,
  isExpanded,
  onToggleExpand,
  onUpdateNotes,
}: {
  client: AdvisorClient;
  onDragStart: (e: React.DragEvent, client: AdvisorClient) => void;
  isExpanded: boolean;
  onUpdateNotes: (id: string, notes: string) => void;
  onToggleExpand: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, client)}
      className={`bg-background border rounded-lg p-3 cursor-grab active:cursor-grabbing transition-colors shadow-sm ${isExpanded ? 'border-amber-500/50 ring-1 ring-amber-500/20' : 'hover:border-amber-500/50'}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggleExpand();
      }}
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <h4
            className="text-sm font-medium truncate cursor-pointer hover:text-amber-600"
            onClick={(e) => { e.stopPropagation(); navigate(`/advisor/clients/${client.id}`); }}
          >
            {client.company_name}
          </h4>
          {client.contact_name && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {client.contact_name}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {client.referral_source && (
              <span className="text-[10px] text-muted-foreground/70 italic truncate max-w-[120px]" title={client.referral_source}>
                via {client.referral_source}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {new Date(client.updated_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      {/* Expanded detail */}
      {isExpanded && (
        <div className="mt-2 pt-2 border-t space-y-2" data-testid="client-card-expanded" onClick={(e) => e.stopPropagation()}>
          {client.contact_email && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Mail className="h-3 w-3" /> {client.contact_email}
            </div>
          )}
          {client.referral_source && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="referral-source">
              <ExternalLink className="h-3 w-3" /> {client.referral_source}
            </div>
          )}
          {client.contact_message && (
            <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2" data-testid="contact-message">
              <MessageSquare className="h-3 w-3 inline mr-1" />
              {client.contact_message}
            </div>
          )}
          <InlineNotes
            notes={client.notes}
            onSave={(notes) => onUpdateNotes(client.id, notes)}
          />

        </div>
      )}
    </div>
  );
}

function KanbanColumn({
  column,
  clients,
  onDragStart,
  onDrop,
  onDragOver,
  showAddForm,
  onToggleAdd,
  expandedCardId,
  onToggleExpand,
  onUpdateNotes,
}: {
  column: typeof KANBAN_COLUMNS[number];
  clients: AdvisorClient[];
  onDragStart: (e: React.DragEvent, client: AdvisorClient) => void;
  onDrop: (e: React.DragEvent, status: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  showAddForm: boolean;
  onToggleAdd: () => void;
  expandedCardId: string | null;
  onToggleExpand: (id: string) => void;
  onUpdateNotes: (id: string, notes: string) => void;
}) {
  return (
    <div
      className={`flex flex-col min-w-[240px] w-full bg-muted/30 rounded-lg border-t-4 ${column.color}`}
      onDrop={(e) => onDrop(e, column.key)}
      onDragOver={onDragOver}
    >
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{column.label}</h3>
          <Badge variant="secondary" className="text-xs h-5 min-w-[20px] justify-center">
            {clients.length}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={onToggleAdd}
          title="Add client"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto max-h-[calc(100vh-280px)]">
        {clients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            onDragStart={onDragStart}
            isExpanded={expandedCardId === client.id}
            onToggleExpand={() => onToggleExpand(client.id)}
            onUpdateNotes={onUpdateNotes}
          />
        ))}
        {clients.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No clients</p>
        )}
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  const updateNotesMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/clients/${id}/`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-clients'] });
    },
  });
  const [createInColumn, setCreateInColumn] = useState('lead');
  const [newCompany, setNewCompany] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');

  const { data: clients, isLoading } = useQuery({
    queryKey: ['advisor-clients'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/clients/');
      return res.data as AdvisorClient[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/clients/', {
        company_name: newCompany,
        contact_name: newContactName,
        contact_email: newContactEmail,
        status: createInColumn,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-clients'] });
      setShowCreateForm(false);
      setNewCompany('');
      setNewContactName('');
      setNewContactEmail('');
      // Navigate to the new client's detail page per engagement story
      if (response?.data?.id) {
        navigate(`/advisor/clients/${response.data.id}/overview`);
      }
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/clients/${id}/`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-clients'] });
    },
  });

  const filtered = clients?.filter((c) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !c.company_name.toLowerCase().includes(q) &&
        !c.contact_name.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const handleDragStart = (e: React.DragEvent, client: AdvisorClient) => {
    e.dataTransfer.setData('text/plain', client.id);
  };

  const handleDrop = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    const clientId = e.dataTransfer.getData('text/plain');
    if (clientId) {
      updateStatusMutation.mutate({ id: clientId, status });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Client Pipeline</h2>
          <p className="text-muted-foreground">Track prospects through your onboarding pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('kanban')}
          >
            <Columns3 className="h-4 w-4 mr-1" />
            Kanban
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            onClick={() => {
              setCreateInColumn('lead');
              setShowCreateForm(true);
            }}
            data-testid="new-client-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Client
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Create form modal */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add New Client</CardTitle>
            <CardDescription>New client will be added to the "{KANBAN_COLUMNS.find((c) => c.key === createInColumn)?.label}" column</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Company name" value={newCompany} onChange={(e) => setNewCompany(e.target.value)} data-testid="new-client-company" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Contact name" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} data-testid="new-client-contact-name" />
              <Input placeholder="Contact email" type="email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} data-testid="new-client-contact-email" />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => createMutation.mutate()} disabled={!newCompany || createMutation.isPending} data-testid="submit-new-client">
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Client
              </Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        <div className="flex gap-3 overflow-x-auto pb-4">
          {KANBAN_COLUMNS.map((column) => {
            const columnClients = (filtered || []).filter((c) => c.status === column.key);
            return (
              <KanbanColumn
                key={column.key}
                column={column}
                clients={columnClients}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                showAddForm={showCreateForm && createInColumn === column.key}
                onToggleAdd={() => {
                  setCreateInColumn(column.key);
                  setShowCreateForm(true);
                }}
                expandedCardId={expandedCardId}
                onToggleExpand={(id) => setExpandedCardId(expandedCardId === id ? null : id)}
                onUpdateNotes={(id, notes) => updateNotesMutation.mutate({ id, notes })}
              />
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {!filtered?.length ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  {clients?.length ? 'No clients match your filters.' : 'No clients yet.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filtered.map((client) => (
              <Card
                key={client.id}
                className="hover:border-amber-500/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/advisor/clients/${client.id}`)}
              >
                <CardContent className="py-3">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-medium text-sm truncate">{client.company_name}</h3>
                        <Badge
                          variant="outline"
                          className={`text-xs ${STATUS_BADGE_COLORS[client.status] || ''}`}
                        >
                          {client.status_display}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {client.contact_name && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {client.contact_name}
                          </span>
                        )}
                        {client.contact_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {client.contact_email}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(client.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Summary */}
      {clients && clients.length > 0 && (
        <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
          <span>{clients.length} client{clients.length !== 1 ? 's' : ''} total</span>
          {KANBAN_COLUMNS.map((col) => (
            <span key={col.key}>
              {clients.filter((c) => c.status === col.key).length} {col.label.toLowerCase()}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
